import * as dropin from "braintree-web-drop-in";
import * as React from "react";
import { connect } from "react-redux";
import { Dispatch } from "redux";
import { RouteComponentProps, withRouter } from "react-router-dom";

import * as analytics from "../../analytics";
import {
  CheckoutTotal,
  PaymentDetails,
} from "../../components/molecules";
import { Modal } from '../../components/themed-v2/molecules/modal';
import { LoaderContainer } from "../../containers/common";
import {
  BookingLocationPresenter,
  inflateBookingLocationPresenter,
} from "../../presenters/booking-location";
import { ApiAddress, fetchUserAddresses } from "../../services/api/addresses";
import { ICategory } from "../../services/api/category-list";
import {
  checkoutBooking,
  ICreateBookingParams,
} from "../../services/api/checkout";
import { IAppConfiguration } from "../../services/api/configuration";
import { get3DSecure, getBraintreeToken } from "../../services/api/payment";
import { IPromotion } from "../../services/api/promotion";
import { IServiceTreatment, ITherapist } from "../../services/api/therapists";
import {
  actionCreators as checkoutActions,
  getCreditBalance,
} from "../../store/checkout/actions";
import { IRootState } from "../../store/index";
import { normalizeObjects } from "../../store/utils";
import { theme } from "../../theme";
import { mapArrayCounter } from "../../utils/index";
import { findAddressUrn } from "../../utils/addresses";
import { parseRakutenCookie } from "../../utils/cookies";
import { getPromoValue } from "../../utils/promo-code";
import { fetchUsersBookings } from "../../services/api/booking";

interface IReduxProps {
  bookingLocation?: BookingLocationPresenter;
  billingLocation?: BookingLocationPresenter;
  categories: ICategory[];
  configuration?: IAppConfiguration;
  creditBalance?: number;
  date?: string;
  isLogedIn: boolean;
  originalAmount?: number;
  promocode?: IPromotion;
  selectedCategory?: ICategory;
  therapist?: ITherapist;
  bookingFee: number;
  totalAmount?: number;
  treatments: IServiceTreatment[];
}

interface IState {
  bookingError?: string;
  btreeClientToken?: string;
  isBookingLoading: boolean;
  isCheckoutDisabled: boolean;
  addresses: ApiAddress[];
}

type RouteProps = RouteComponentProps;
type Props = IReduxProps & RouteProps & { dispatch: Dispatch<any> };

class PaymentDetailsContainer extends React.Component<Props, IState> {
  private dropinInstanceRef?: dropin.Dropin;
  private readonly dropInRef = React.createRef<HTMLElement>();
  private readonly checkoutRef = React.createRef<HTMLElement>();

  /**
   * Set event listener for checkout button based on total amount
   * Paid with braintree or free
   */
  private checkAmountForBraintree() {
    const lastChild = this.checkoutRef.current?.lastChild;

    if (this.props.totalAmount === 0 && this.props.originalAmount > 0) {
      if (
        this.props.originalAmount >=
        this.props.configuration.BOOKING_MINIMUM_FEE
      ) {
        this.setState({ isCheckoutDisabled: false });
      }

      if (lastChild) {
        lastChild.removeEventListener("click", this.checkoutWithPayment);
        lastChild.addEventListener("click", this.checkoutFree);
      }
    } else {
      if (this.dropinInstanceRef && this.props.totalAmount) {
        // update total for DropIn UI
        this.dropinInstanceRef.updateConfiguration(
          "applePay",
          "paymentRequest",
          {
            requiredBillingContactFields: ["postalAddress"],
            total: {
              amount: this.props.totalAmount.toString(),
              label: "Ruuby",
            },
          },
        );

        if (lastChild) {
          lastChild.removeEventListener("click", this.checkoutFree);
          lastChild.addEventListener("click", this.checkoutWithPayment);
        }
      }
    }
  }

  public checkoutNotes?: string;

  public state: IState = {
    bookingError: undefined,
    btreeClientToken: undefined,
    isBookingLoading: false,
    isCheckoutDisabled: true,
    addresses: [],
  };

  public async componentDidMount() {
    this.setState({
      addresses: await fetchUserAddresses(),
    });

    if (this.props.isLogedIn) {
      await this.initDropIn();
    }
  }

  public async componentDidUpdate(prevProps: Props) {
    if (prevProps.isLogedIn !== this.props.isLogedIn && this.props.isLogedIn) {
      await this.initDropIn();
    }

    if (prevProps.totalAmount !== this.props.totalAmount) {
      this.checkAmountForBraintree();
    }
  }

  public setNotes = (e: React.FocusEvent<HTMLTextAreaElement>) => {
    this.checkoutNotes = e.currentTarget.value;
  };

  /**
   * Checkout/Payment and redirect to booking confirmation page
   * @param payload braintree
   */
  // tslint:disable-next-line: no-any
  public async proccedCheckout(payload?: dropin.PaymentMethodPayload) {
    // tslint:disable-next-line: no-unsafe-any
    const paymentMethodNonce = payload ? payload.nonce : undefined;
    const rakutenCookie = parseRakutenCookie(document.cookie);

    const params: ICreateBookingParams = {
      address: this.props.bookingLocation.getCheckoutAddress(),
      bookingDate: this.props.date,
      notes: this.checkoutNotes,
      origin: "WEBSITE",
      paymentMethodNonce,
      promoCode:
        this.props.promocode && this.props.promocode.isValid
          ? this.props.promocode.code
          : undefined,
      serviceUrns: mapArrayCounter(this.props.treatments, "count", "urn"),
      therapistUrn: this.props.therapist.urn,
    };

    if (typeof rakutenCookie !== "undefined") {
      params.rakutenSiteid = rakutenCookie.atrv;
      params.rakutenTimeEntered = new Date(rakutenCookie.auld).toISOString();
    }

    try {
      const bookingResponse = await checkoutBooking(params);
      const bookingUrn = bookingResponse.booking.urn;
      const bookingMessage = bookingResponse.message;

      // TODO: temporaly
      // ! this is an additional check in case API is not returning the error because of the failed Apple Pay
      if (!bookingUrn) {
        this.setState({
          bookingError: "Couldn't create booking, please call 0207 460 8319",
          isBookingLoading: false,
        });

        return;
      }

      const { totalAmount } = this.props;

      const paymentMethod = () => {
        if (payload && this.props.totalAmount) {
          return payload.type === "CreditCard"
            ? { payment: `${payload.details.cardType}` }
            : payload.type === "ApplePayCard"
            ? { payment: "Apple Pay" }
            : undefined;
        } else {
          return undefined;
        }
      };

      const state = {
        address: this.props.bookingLocation.getSummaryAddress(),
        categoryImage: this.props.selectedCategory
          ? this.props.selectedCategory.image
          : this.props.categories[0].image,
        date: this.props.date,
        id: bookingUrn,
        ...paymentMethod(),
        therapist: this.props.therapist.therapistName,
        totalAmount,
        treatments: this.props.treatments.map((treatment) => treatment.name),
        bookingMessage: bookingMessage,
      };

      let isNewCustomer = true;

      try {
        const bookings = await fetchUsersBookings();
        isNewCustomer =
          bookings.completed.length + bookings.upcoming.length < 2;
      } finally {
        const treatmentValue = this.props.treatments.reduce(
          (acc, t) => acc + t.price * t.count,
          0,
        );

        analytics.trackBookingSuccess({
          bookingUrn,
          treatments: this.props.treatments,
          bookingFee: this.props.bookingFee,
          isNewCustomer,
          ...(this.props.promocode && {
            promoCode: this.props.promocode.code,
            promoCodeAmount: getPromoValue(this.props.promocode, treatmentValue)
              .deduction,
          }),
        });
      }

      this.props.dispatch(checkoutActions.promocodeClear());
      this.props.history.replace("/booking/confirmation", state);
    } catch (error) {
      if (error instanceof Error) {
        this.setState({ bookingError: error.message, isBookingLoading: false });

        analytics.track({
          name: analytics.AnalyticsEvents.BOOKING_FAIL,
          payload: {
            label: error.message,
          },
        });
      }
    }
  }

  /**
   * Init braintree widget for card payment
   */
  private async initDropIn() {
    if (this.props.creditBalance === undefined) {
      this.props.dispatch(getCreditBalance());
    }

    const token = await getBraintreeToken();

    this.setState({ btreeClientToken: token }, async () => {
      try {
        if (this.state.btreeClientToken && this.dropInRef.current && this.props.totalAmount) {
          this.dropinInstanceRef = await dropin.create({
            applePay: {
              displayName: "Ruuby",
              paymentRequest: {
                requiredBillingContactFields: ["postalAddress"],
                total: {
                  amount: this.props.totalAmount.toString(),
                  label: "Ruuby",
                },
                countryCode: "GB",
                currencyCode: "GBP",
                supportedNetworks: ["visa", "masterCard", "amex"],
                merchantCapabilities: ["supports3DS", "supportsCredit"],
              },
            },
            authorization: this.state.btreeClientToken,
            card: {
              cardholderName: { required: true },
              overrides: {
                fields: {
                  cvv: {
                    placeholder: "Security Code",
                    type: "password",
                  },
                },
                styles: {
                  input: {
                    color: `${theme.color.ruubyBlack}`,
                    "font-size": "14px",
                    "font-weight": 400,
                    height: "48px",
                  },
                },
              },
            },
            container: this.dropInRef.current,
            threeDSecure: true,
            vaultManager: true,
          });

          this.checkAmountForBraintree();

          if (this.dropinInstanceRef.isPaymentMethodRequestable()) {
            this.setState({ isCheckoutDisabled: false });
          }

          this.dropinInstanceRef.on("paymentMethodRequestable", () => {
            this.setState({ isCheckoutDisabled: false });
          });
          this.dropinInstanceRef.on("noPaymentMethodRequestable", () => {
            this.setState({ isCheckoutDisabled: true });
          });
        }
      } catch (error) {
        if (error instanceof Error) {
          this.setState({
            bookingError: error.message,
            isBookingLoading: false,
          });
        }
      }
    });
  }

  public clearError = () => {
    this.setState({ bookingError: undefined });
  };

  /**
   * Include payload for braintree checkout
   * Pay with card
   */
  public checkoutWithPayment = async () => {
    this.setState({ isBookingLoading: true });

    const billingCheckoutAddress =
      this.props.billingLocation.getCheckoutAddress();
    const billingAddressUrn = findAddressUrn(
      billingCheckoutAddress,
      this.state.addresses,
    );
    const bookingCheckoutAddress =
      this.props.bookingLocation.getCheckoutAddress();
    const bookingAddressUrn = findAddressUrn(
      bookingCheckoutAddress,
      this.state.addresses,
    );

    if (billingAddressUrn && bookingAddressUrn) {
      const threeDSecure = await get3DSecure({
        amount: this.props.totalAmount,
        billingAddressUrn,
        bookingAddressUrn,
        therapistUrn: this.props.therapist.urn,
        treatmentUrns: this.props.treatments.map((treatment) => treatment.urn),
        bookingTime: new Date().toISOString(),
      });

      if (this.dropinInstanceRef) {
        try {
          const payload = await this.dropinInstanceRef.requestPaymentMethod({
            threeDSecure: {
              ...threeDSecure,
              amount: threeDSecure.amount.toFixed(2),
            },
          });

          await this.proccedCheckout(payload);
        } catch (error) {
          if (error instanceof Error) {
            this.setState({
              bookingError: error.message,
              isBookingLoading: false,
            });
          }
        }
      }
    }
  };

  /**
   * No need for braintree
   * Treatments for free!
   */
  public checkoutFree = async () => {
    this.setState({ isBookingLoading: true });
    await this.proccedCheckout();
  };

  /**
   * Render payment details block
   */
  public render() {
    return (
      <React.Fragment>
        {this.props.isLogedIn && (
          <React.Fragment>
            {!Boolean(this.state.btreeClientToken) ? (
              <LoaderContainer type="block" />
            ) : (
              <PaymentDetails
                innerRef={this.dropInRef}
                totalAmount={this.props.totalAmount}
              />
            )}
            <CheckoutTotal
              innerRef={this.checkoutRef}
              isCheckoutDisabled={this.state.isCheckoutDisabled}
              originalAmount={this.props.originalAmount}
              totalAmount={this.props.totalAmount}
              minimumAmount={this.props.configuration.BOOKING_MINIMUM_FEE}
              updateNotes={this.setNotes}
            />
          </React.Fragment>
        )}
        {this.state.isBookingLoading && <LoaderContainer type="overlay" />}
        <Modal
          isShow={Boolean(this.state.bookingError)}
          onClose={this.clearError}
          modalText={this.state.bookingError}
        />
      </React.Fragment>
    );
  }
}

// store
const mapStateToProps = (state: IRootState): IReduxProps => {
  return {
    ...(state.postcodesState.bookingLocation && {
      bookingLocation: inflateBookingLocationPresenter(
        state.postcodesState.bookingLocation,
      ),
    }),
    ...(state.postcodesState.billingLocation && {
      billingLocation: inflateBookingLocationPresenter(
        state.postcodesState.billingLocation,
      ),
    }),
    categories: state.categoriesState.categories,
    configuration: state.configurationState.configuration,
    creditBalance: state.checkoutState.creditBalance,
    date: state.therapistsState.selectedDate,
    isLogedIn: state.userState.isLogedIn,
    originalAmount: state.checkoutState.originalAmount,
    promocode: state.checkoutState.promocode,
    selectedCategory: state.categoriesState.selectedCategory,
    therapist: state.therapistsState.selectedTherapist,
    bookingFee: state.checkoutState.bookingFee,
    totalAmount: state.checkoutState.totalAmount,
    treatments: normalizeObjects(state.treatmentsState.treatments),
  };
};

const store = withRouter(
  connect<IReduxProps, {}, {}, IRootState>(mapStateToProps)(
    PaymentDetailsContainer,
  ),
);

export { store as PaymentDetailsContainer };
